// @ts-check
import {convertImageToWebP} from '@/helpers/shadcn/utils'
import {uploadImagesService} from '@/services/uploadImages'
import {useCallback, useState} from 'react'

const MAX_SIZE_MB = 2
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

/**
 * @param {{entityType?: string, id?: string}} [options]
 */
export const useImageUpload = (options = {}) => {
  const {entityType = 'product', id} = options

  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(/** @type {string | null} */ (null))

  /**
   * Upload an image file: validates size, converts to WebP, uploads to API.
   * @param {File} file
   * @returns {Promise<{url: string, id: string} | null>} The uploaded image CDN URL and id, or null on failure.
   */
  const upload = useCallback(
    async file => {
      if (!file) return null

      if (file.size > MAX_SIZE_BYTES) {
        setError(`Image must be ${MAX_SIZE_MB}MB or less.`)
        return null
      }

      try {
        setError(null)
        setUploading(true)
        setProgress(0)

        const fileToConvert = id
          ? new File([file], `${id}.${file.type.replace('image/', '')}`, {
              type: file.type,
              lastModified: file.lastModified,
            })
          : file

        const converted = await convertImageToWebP(fileToConvert, {entityType})

        if (!converted?.webpFile || !converted?.metadata) {
          setError('Failed to process image.')
          return null
        }

        const formData = new FormData()
        formData.append('images', converted.webpFile)
        formData.append('metadata', JSON.stringify({mediaUploadMetadata: [converted.metadata]}))

        const onUploadProgress = progressEvent => {
          setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
        }

        const response = await uploadImagesService({formData, onUploadProgress})

        if (response?.status === 'SUCCESS') {
          const data = response.data?.[0]
          return {url: data?.imageCDNUrl, id: data?.id}
        }

        setError('Upload failed. Please try again.')
        return null
      } catch (err) {
        setError(err?.message || err?.data?.data?.[0]?.errors?.[0] || 'Upload failed.')
        return null
      } finally {
        setUploading(false)
      }
    },
    [entityType, id]
  )

  const clearError = useCallback(() => setError(null), [])

  return {upload, uploading, progress, error, clearError}
}
